import { useEffect, useRef, useState } from 'react';

/**
 * Phase 6e1 — shared items pager (§7.7e, items_list.tmpl markup faithful):
 * numbered chips + prev/next step buttons. Used by the dmount + int-wall
 * groups.
 *
 * QA5 #8 — reworked to the LEGACY windowing semantics
 * (tab.section.internal_wall.js:486-523 showOrHideButtons/prev_next, same
 * copy in tab.section.js for dmounts):
 *  - arrows carry the `hide` class unless there are MORE than 4 items
 *    (they were previously always visible);
 *  - the arrows scroll a 4-chip WINDOW (first/last visible) without changing
 *    the selection; chips outside the window get the `hide` class;
 *  - `non-clickable` at the window ends;
 *  - a newly added item shifts the window to the end (legacy render(isNew));
 *    a deletion resets it (legacy nulls first/last_visble).
 */

interface ItemsPagerProps {
  ids: number[];
  activeId: number | null;
  onSelect: (id: number) => void;
  className?: string;
}

const VISIBLE = 4;

const ItemsPager = ({ ids, activeId, onSelect, className }: ItemsPagerProps) => {
  const [first, setFirst] = useState(1); // 1-based window start
  const prevLen = useRef(ids.length);
  useEffect(() => {
    if (ids.length > prevLen.current && prevLen.current > 0) {
      // legacy render(isNew) — new item shifts the window to the end. The
      // prevLen>0 guard keeps the initial data load (0→N) at window 1..4
      // like a legacy fresh render.
      setFirst(Math.max(1, ids.length - VISIBLE + 1));
    } else if (ids.length < prevLen.current) {
      // legacy deletion — window resets to the start.
      setFirst(1);
    }
    prevLen.current = ids.length;
  }, [ids.length]);

  if (!ids.length) return <div className={className} />;
  const overflow = ids.length > VISIBLE;
  const last = first + VISIBLE - 1;
  const canPrev = overflow && first !== 1;
  const canNext = overflow && last < ids.length;
  const step = (delta: number) => setFirst((f) => f + delta);

  return (
    <div className={className}>
      <button
        id="prev_item"
        className={`icon-angle-left prev_next_item${canPrev ? '' : ' non-clickable'}${
          overflow ? '' : ' hide'
        }`}
        data-step="-1"
        onClick={() => canPrev && step(-1)}
      />
      {ids.map((id, idx) => {
        const hidden = overflow && (idx + 1 < first || idx + 1 > last);
        return (
          <div
            key={id}
            className={`item item_${id}${id === (activeId ?? ids[0]) ? ' active' : ''}${
              hidden ? ' hide' : ''
            }`}
            data-id={id}
            onClick={() => onSelect(id)}
          >
            {idx + 1}
          </div>
        );
      })}
      <button
        id="next_item"
        className={`icon-angle-right prev_next_item${canNext ? '' : ' non-clickable'}${
          overflow ? '' : ' hide'
        }`}
        data-step="1"
        onClick={() => canNext && step(1)}
      />
    </div>
  );
};

export default ItemsPager;
